import { useState } from 'react';
import Button from '../../../shared/components/Button';
import { Input } from '../../../shared/components/Input';
import { Modal } from '../../../shared/components/Modal';
import { PriceInput } from '../../../shared/components/PriceInput';
import { Select } from '../../../shared/components/Select';
import { Toggle } from '../../../shared/components/Toggle';
import { useCreateReferralProgram, useUpdateReferralProgram } from '../hooks/useReferrals';
import {
  AMOUNT_TYPE_LABELS,
  REWARD_TYPE_LABELS,
  RewardAmountType,
  RewardTriggerCondition,
  RewardType,
  TRIGGER_LABELS,
  type ReferralRewardProgram,
} from '../types';

interface Props {
  /** Absent = create mode. */
  program?: ReferralRewardProgram;
  onClose: () => void;
}

const optionsFrom = (labels: Record<string, string>) =>
  Object.entries(labels).map(([value, title]) => ({ value, title }));

/**
 * Create or edit a reward rule: what the referrer earns (type), how much (fixed kobo or a
 * percentage of the deal), and when (trigger). osprey-api 400s a FIXED rule without
 * amountMinor or a PERCENTAGE rule without percentage, so only the field that matches the
 * amount type is shown and sent.
 *
 * "Default" makes this the platform rule for any code without its own — setting it demotes the
 * previous default server-side.
 */
export function ProgramFormModal({ program, onClose }: Props) {
  const isEdit = !!program;
  const create = useCreateReferralProgram();
  const update = useUpdateReferralProgram();

  const [name, setName] = useState(program?.name ?? '');
  const [rewardType, setRewardType] = useState<RewardType>(
    program?.rewardType ?? RewardType.CASH_COMMISSION,
  );
  const [amountType, setAmountType] = useState<RewardAmountType>(
    program?.amountType ?? RewardAmountType.FIXED,
  );
  const [amountMinor, setAmountMinor] = useState<number>(program?.amountMinor ?? 0);
  const [percentage, setPercentage] = useState(
    program?.percentage != null ? String(program.percentage) : '',
  );
  const [triggerCondition, setTriggerCondition] = useState<RewardTriggerCondition>(
    program?.triggerCondition ?? RewardTriggerCondition.CONTRACT_SIGNED,
  );
  const [isDefault, setIsDefault] = useState(program?.isDefault ?? false);
  const [isActive, setIsActive] = useState(program?.isActive ?? true);

  const pct = Number(percentage);
  const amountValid =
    amountType === RewardAmountType.FIXED
      ? Number.isInteger(amountMinor) && amountMinor > 0
      : percentage.trim() !== '' && pct > 0 && pct <= 100;
  const valid = name.trim().length > 0 && amountValid;
  const isPending = create.isPending || update.isPending;

  const submit = async () => {
    if (!valid) return;
    const payload = {
      name: name.trim(),
      rewardType,
      amountType,
      triggerCondition,
      isDefault,
      ...(amountType === RewardAmountType.FIXED ? { amountMinor } : { percentage: pct }),
    };
    if (isEdit) {
      await update.mutateAsync({ id: program.id, payload: { ...payload, isActive } });
    } else {
      await create.mutateAsync(payload);
    }
    onClose();
  };

  return (
    <Modal isOpen onClose={onClose} size="md">
      <div className="p-6 space-y-5 max-h-[80vh] overflow-y-auto themed-scrollbar">
        <h2 className="text-[18px] font-semibold text-foreground">
          {isEdit ? 'Edit reward rule' : 'New reward rule'}
        </h2>

        <Input
          className="min-h-10 lg:min-h-0"
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Merchant referral — ₦50k on signing"
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Select
            label="Reward type"
            options={optionsFrom(REWARD_TYPE_LABELS)}
            value={rewardType}
            onChange={(v) => setRewardType(v as RewardType)}
          />
          <Select
            label="Trigger"
            options={optionsFrom(TRIGGER_LABELS)}
            value={triggerCondition}
            onChange={(v) => setTriggerCondition(v as RewardTriggerCondition)}
          />
          <Select
            label="Amount"
            options={optionsFrom(AMOUNT_TYPE_LABELS)}
            value={amountType}
            onChange={(v) => setAmountType(v as RewardAmountType)}
          />
          {amountType === RewardAmountType.FIXED ? (
            <PriceInput
              label="Amount (₦)"
              className="min-h-10 lg:min-h-0"
              value={amountMinor}
              onChange={setAmountMinor}
            />
          ) : (
            <Input
              className="min-h-10 lg:min-h-0"
              label="Percentage of deal"
              type="number"
              min={0}
              max={100}
              step="0.1"
              value={percentage}
              onChange={(e) => setPercentage(e.target.value)}
              placeholder="10"
            />
          )}
        </div>

        {amountType === RewardAmountType.PERCENTAGE && (
          <p className="text-[12px] text-muted-foreground">
            Worked out from the referred deal's total when the trigger fires.
          </p>
        )}

        <Toggle
          label="Platform default — applies to codes without their own rule"
          checked={isDefault}
          onChange={setIsDefault}
        />
        {isEdit && <Toggle label="Active" checked={isActive} onChange={setIsActive} />}

        <div className="flex justify-end gap-3">
          <Button variant="outline" size="sm" className="h-[40px]" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            className="h-[40px]"
            onClick={submit}
            disabled={!valid || isPending}
            loading={isPending}
          >
            {isEdit ? 'Save rule' : 'Create rule'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
